import React, { useState, useEffect } from 'react';
import { ChevronRight, Link as LinkIcon } from 'lucide-react';
import { useI18n } from '../hooks/useI18n';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Fixed height to match Key Offerings
const FIXED_HEIGHT = '400px';

// Default links matching original CISH website
const defaultLinks = [
  { name: 'eHRMS 2.0', url: 'https://e-hrms.gov.in/login' },
  { name: 'DARE/ICAR e-Office', url: 'https://eoffice.icar.gov.in/SSOComponent/auth.php' },
  { name: 'DARE/ICAR Sparrow', url: 'https://sparrow.icar.gov.in/' },
  { name: 'NIC mail', url: 'https://mail.gov.in/' },
  { name: 'ICAR', url: 'https://icar.org.in/en' },
  { name: 'NHB', url: 'https://www.nhb.gov.in/' },
  { name: 'APEDA', url: 'https://apeda.gov.in/' },
  { name: 'GeM Portal', url: 'https://gem.gov.in/' },
  { name: 'PFMS', url: 'https://pfms.nic.in/' },
  { name: 'Digital India', url: 'https://digitalindia.gov.in/' },
];

export default function UsefulLinks({ links: propLinks }) {
  const { t } = useI18n();
  const [links, setLinks] = useState(() => {
    // Initialize with propLinks if provided, otherwise use default
    return propLinks && propLinks.length > 0 ? propLinks : defaultLinks;
  });

  // Fetch links from API if not provided as props
  useEffect(() => {
    // Skip fetching if propLinks are already provided
    if (propLinks && propLinks.length > 0) {
      return;
    }

    const fetchLinks = async () => {
      try {
        const response = await fetch(`${API_URL}/api/useful_links`);
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            setLinks(data);
          }
        }
      } catch (error) {
        console.error('Error fetching useful links:', error);
        // Keep default links on error
      }
    };

    fetchLinks();
  }, [propLinks]);

  return (
    <div 
      className="bg-white rounded-lg border border-gray-200 flex flex-col" 
      style={{ height: FIXED_HEIGHT }}
      data-testid="useful-links"
    >
      {/* Section heading - Green background to match Key Offerings */}
      <div className="flex items-center gap-2 px-4 py-3 bg-[#E8F5E9] rounded-t-lg border-b border-gray-200 flex-shrink-0">
        <LinkIcon className="w-5 h-5 text-[#0E6D2B]" aria-hidden="true" />
        <h2 className="text-sm md:text-base font-bold text-[#0E6D2B] uppercase">{t('usefulLinks')}</h2>
      </div>

      {/* Scrollable links list - fills remaining space */}
      <div className="flex-1 overflow-y-auto custom-scroll">
        {links.map((link, index) => (
          <a
            key={link.id || index}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between px-4 py-3 border-b border-gray-50 last:border-b-0 hover:bg-[#F4F8EF] transition-colors group"
            data-testid={`useful-link-${index}`}
          >
            <span className="text-sm text-gray-700 group-hover:text-[#0E6D2B] font-medium transition-colors">
              {link.name}
            </span>
            <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#0E6D2B] group-hover:translate-x-0.5 transition-all" />
          </a>
        ))}
      </div>
    </div>
  );
}
